import { FC } from 'react';
import {
  WellReviewMatch as SharedWellReviewMatch,
  type WellReviewConfig,
} from 'pages/documents/structuring/well-review-match';
import type { StagedWell, WellListItem } from './types-well';
import {
  confirmMatchAtom,
  createWellDetailResourceAtom,
  removeWellAtom,
  rerunSuggestionsAtom,
  restoreWellAtom,
  revertMatchAtom,
  wellDetailUrl,
} from './atoms/wells';
import { WellEditPanel } from './well-edit-panel';
import { ChangeMatchPanel } from './change-match-panel';
import { AgentAuditLog } from './agent-audit-log';

// RC Well Match Review — a thin doctype wrapper over the shared drill-in
// (pages/documents/structuring/well-review-match). All behavior lives in the
// shared component; this file supplies the RC wiring:
//   • the RC wells atom bundle + URL builders (numeric RoyaltyCheckTarget PK);
//   • the RC staged shape (operator / propertyName / propertyNumber) and none of
//     the AFE-only / DO-only tile fields;
//   • the remove URL built by id — the RC show emits confirmation /
//     matchCandidate / suggestionRerun / restoration but NOT update / removal, so
//     no edit href is supplied either (the panel builds it by id);
//   • the `.rc-structuring-shell` slide-in selector;
//   • the RC Edit / ChangeMatch panels + the RC activity log (section='well',
//     scoped by royaltyCheckTargetId).

// Stable module-level config — a fresh object each render would re-create the
// detail resource atom (via useMemo) and refetch on every render.
const RC_WELL_REVIEW_CONFIG: WellReviewConfig<StagedWell> = {
  createWellDetailResourceAtom,
  wellDetailUrl,
  confirmMatchAtom,
  revertMatchAtom,
  rerunSuggestionsAtom,
  removeWellAtom,
  restoreWellAtom,
  // RC staged shape only. Hole Direction / Well Status aren't extracted from RC
  // documents (they come from the well registry on match), so they always render
  // '—' — the row exists purely for alignment with the suggestion tiles.
  stagedTileFields: (staged) => [
    { label: 'UWI / API Number', value: staged.uwiApi ?? '—' },
    { label: 'Hole Direction', value: '—' },
    { label: 'Well Status', value: '—' },
    { label: 'County/State', value: staged.countyState ?? '—' },
    { label: 'Operator', value: staged.operator ?? '—' },
    { label: 'Legal Description', value: staged.legalDescription ?? '—' },
    { label: 'Property Name', value: staged.propertyName ?? '—' },
    { label: 'Property Number', value: staged.propertyNumber ?? '—' },
  ],
  // The RC show does NOT emit a removal href — build the canonical DELETE
  // .../wells/:id route by id (detailHref already points at it).
  removalUrl: (_detail, detailHref) => detailHref,
  // No `wellEditHref` — the RC WellEditPanel builds the PATCH URL by id.
  shellSelector: '.rc-structuring-shell',
  WellEditPanel,
  ChangeMatchPanel,
  renderAuditLog: (documentId, wellId) => (
    <AgentAuditLog
      documentId={documentId}
      section="well"
      royaltyCheckTargetId={wellId}
    />
  ),
};

interface Props {
  documentId: number | string;
  /** The overview row for this well (gives us the id + HATEOAS hrefs). */
  well: WellListItem;
  index: number;
  /** Total well count (kept for a stable export signature). */
  total: number;
  /** Refetch the overview list after any mutation. */
  onMutated: () => void;
}

export const WellReviewMatch: FC<Props> = (props) => (
  <SharedWellReviewMatch<StagedWell>
    {...props}
    config={RC_WELL_REVIEW_CONFIG}
  />
);
